/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { BiLoaderAlt } from "react-icons/bi";
import ReactApexChart from "react-apexcharts";
import { trackingStats } from "../../../../stores/actions/tracking.actions.types";
import MetaHead from "../../../Common/Metahead";
import DateFilter, { getDateParams } from "../../../Common/DateFilter";
import Sorting from "../../../Common/Sorting";

const sourceValues = [
  { label: "All Sources", value: "" },
  { label: "Google Ads", value: "google_ads" },
  { label: "Facebook", value: "facebook" },
  { label: "Meta", value: "meta" },
  { label: "Microsoft Ads", value: "microsoft_ads" },
  { label: "Organic", value: "organic" },
  { label: "Direct", value: "direct" },
  { label: "Email", value: "email" },
  { label: "Referral", value: "referral" },
  { label: "TikTok", value: "tiktok" },
  { label: "Twitter", value: "twitter" },
];

const eventTypeBadge = {
  page_view: "bg-blue-100 text-blue-700",
  product_view: "bg-purple-100 text-purple-700",
  add_to_cart: "bg-green-100 text-green-700",
  remove_from_cart: "bg-red-100 text-red-700",
  update_cart: "bg-yellow-100 text-yellow-700",
  search: "bg-gray-100 text-gray-700",
  click: "bg-orange-100 text-orange-700",
  checkout_complete: "bg-emerald-100 text-emerald-700",
};

const eventColors = [
  "#3b82f6",
  "#8b5cf6",
  "#22c55e",
  "#ef4444",
  "#eab308",
  "#6b7280",
  "#f97316",
  "#10b981",
];

const TrackingStats = (props) => {
  const [stats, setStats] = useState([]);
  const [source, setSource] = useState("");
  const [dateParams, setDateParams] = useState(getDateParams("today"));
  const [isDark, setIsDark] = useState(
    document.documentElement.classList.contains("dark")
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const params = { ...dateParams };
    if (source) params.source = source;
    props.trackingStats({
      data: params,
      callback: (data) => {
        if (data.success) setStats(data.data.stats);
      },
    });
  }, [dateParams, source]);

  const labelColor = isDark ? "#e2e8f0" : "#4b5563";
  const gridColor = isDark ? "#374151" : "#e5e7eb";

  const shortLabels = {
    page_view: "Page View",
    product_view: "Prod View",
    add_to_cart: "Add Cart",
    remove_from_cart: "Rem Cart",
    update_cart: "Upd Cart",
    search: "Search",
    click: "Click",
    checkout_complete: "Checkout",
  };

  const categories = stats.map((s) =>
    s.eventType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );

  const shortCategories = stats.map((s) => shortLabels[s.eventType] || s.eventType);

  const chartOptions = {
    chart: {
      type: "bar",
      fontFamily: "Satoshi, sans-serif",
      toolbar: { show: false },
      background: "transparent",
    },
    theme: {
      mode: isDark ? "dark" : "light",
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: "55%",
        distributed: true,
      },
    },
    colors: eventColors,
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: {
      borderColor: gridColor,
      strokeDashArray: 4,
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } },
    },
    xaxis: {
      categories: shortCategories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotate: 0,
        style: {
          fontSize: "11px",
          colors: Array(shortCategories.length).fill(labelColor),
        },
      },
    },
    yaxis: {
      labels: {
        style: { colors: [labelColor] },
        formatter: (val) => val.toLocaleString(),
      },
    },
    tooltip: {
      custom: ({ series, seriesIndex, dataPointIndex }) => {
        const label = categories[dataPointIndex];
        const value = series[seriesIndex][dataPointIndex];
        const color = eventColors[dataPointIndex] || "#6b7280";
        const bg = isDark ? "#1f2937" : "#ffffff";
        const border = isDark ? "#374151" : "#e5e7eb";
        const textColor = isDark ? "#e2e8f0" : "#374151";
        return `<div style="padding:10px 14px;background:${bg};border:1px solid ${border};border-radius:8px;min-width:160px;">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
            <span style="width:10px;height:10px;border-radius:50%;background:${color};display:inline-block;flex-shrink:0;"></span>
            <span style="font-weight:600;font-size:13px;color:${textColor};">${label}</span>
          </div>
          <div style="font-size:13px;color:${textColor};">Count: <strong>${value.toLocaleString()}</strong></div>
        </div>`;
      },
    },
  };

  const totalEventsSeries = [
    {
      name: "Total Events",
      data: stats.map((s) => s.count),
    },
  ];

  const uniqueUsersSeries = [
    {
      name: "Unique Users",
      data: stats.map((s) => s.uniqueUserCount),
    },
  ];

  return (
    <div>
      <MetaHead title='Tracking Stats' />
      <div className='flex items-center justify-end mb-3 gap-2'>
        <Sorting
          name='Source'
          values={sourceValues}
          onChange={setSource}
        />
        <DateFilter onChange={setDateParams} />
      </div>

      {props.loading ? (
        <div className='flex dark:bg-boxdark bg-white items-center justify-center min-h-96'>
          <BiLoaderAlt className='animate-spin text-4xl text-primary dark:text-white' />
        </div>
      ) : stats?.length > 0 ? (
        <>
          {/* Charts */}
          <div className='grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6'>
            {/* Total Events Chart */}
            <div className='bg-white dark:bg-boxdark border border-stroke rounded-[10px] px-6 pt-6 pb-2'>
              <h4 className='text-sm font-semibold text-gray-800 dark:text-white mb-4'>
                Total Events by Type
              </h4>
              <ReactApexChart
                options={chartOptions}
                series={totalEventsSeries}
                type='bar'
                height={280}
              />
            </div>

            {/* Unique Users Chart */}
            <div className='bg-white dark:bg-boxdark border border-stroke rounded-[10px] px-6 pt-6 pb-2'>
              <h4 className='text-sm font-semibold text-gray-800 dark:text-white mb-4'>
                Unique Users by Event Type
              </h4>
              <ReactApexChart
                options={{
                  ...chartOptions,
                  tooltip: {
                    custom: ({ series, seriesIndex, dataPointIndex }) => {
                      const label = categories[dataPointIndex];
                      const value = series[seriesIndex][dataPointIndex];
                      const color = eventColors[dataPointIndex] || "#6b7280";
                      const bg = isDark ? "#1f2937" : "#ffffff";
                      const border = isDark ? "#374151" : "#e5e7eb";
                      const textColor = isDark ? "#e2e8f0" : "#374151";
                      return `<div style="padding:10px 14px;background:${bg};border:1px solid ${border};border-radius:8px;min-width:160px;">
                        <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
                          <span style="width:10px;height:10px;border-radius:50%;background:${color};display:inline-block;flex-shrink:0;"></span>
                          <span style="font-weight:600;font-size:13px;color:${textColor};">${label}</span>
                        </div>
                        <div style="font-size:13px;color:${textColor};">Users: <strong>${value}</strong></div>
                      </div>`;
                    },
                  },
                }}
                series={uniqueUsersSeries}
                type='bar'
                height={280}
              />
            </div>
          </div>

          {/* Table */}
          <div className='rounded-[10px] bg-white border border-stroke p-4 dark:bg-boxdark'>
            <div className='max-w-full overflow-x-auto'>
              <table className='w-full table-auto'>
                <thead>
                  <tr className='bg-[#F7F9FC] dark:bg-black text-left'>
                    <th className='min-w-[180px] px-4 py-2 font-medium text-sm'>Event Type</th>
                    <th className='min-w-[120px] px-4 py-2 font-medium text-sm'>Total Events</th>
                    <th className='min-w-[120px] px-4 py-2 font-medium text-sm'>Unique Users</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.map((item, index) => (
                    <tr key={index} className='text-sm'>
                      <td className='border-b px-4 py-2 dark:border-strokedark'>
                        <div className='flex items-center gap-2'>
                          <span
                            className='w-2.5 h-2.5 rounded-full shrink-0'
                            style={{ backgroundColor: eventColors[index] }}
                          />
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              eventTypeBadge[item.eventType] || "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {item.eventType}
                          </span>
                        </div>
                      </td>
                      <td className='border-b px-4 py-2 dark:border-strokedark font-semibold'>
                        {item.count.toLocaleString()}
                      </td>
                      <td className='border-b px-4 py-2 dark:border-strokedark'>
                        {item.uniqueUserCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className='flex dark:bg-boxdark rounded bg-white items-center justify-center min-h-96'>
          <p className='text-xl text-center text-primary'>No Stats Found</p>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  loading: state.app.visible,
});

export default connect(mapStateToProps, { trackingStats })(TrackingStats);
